import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast as sonnerToast } from 'sonner';
import { Check, CreditCard, Sparkles } from 'lucide-react';
import type { MemberRole, MeResponse } from '@seo/types';
import { Page, PageHeader } from '@/components/ui/page';
import { SettingsTabs } from '@/components/settings-tabs';
import { Surface } from '@/components/ui/surface';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/spinner';
import { useAuthStore } from '@/lib/auth';
import { apiGet, apiPost } from '@/lib/api';
import { useToast, toastError } from '@/components/ui/toast';
import { cn } from '@/lib/cn';

interface CheckoutResponse {
  url: string;
}
interface CheckoutBody {
  plan: 'audit' | 'content' | 'copilot';
  success_url?: string;
  cancel_url?: string;
}

const PLANS: Array<{
  key: CheckoutBody['plan'];
  name: string;
  tagline: string;
  features: string[];
  highlighted?: boolean;
}> = [
  {
    key: 'audit',
    name: 'Audit',
    tagline: 'Crawl + score, every week.',
    features: [
      'Unlimited site audits',
      'Findings + recommendations',
      'PDF exports',
      '1 workspace',
    ],
  },
  {
    key: 'content',
    name: 'Content',
    tagline: 'Briefs + calendars.',
    features: [
      'Everything in Audit',
      'Unlimited briefs',
      'Content calendar generation',
      'Up to 3 workspaces',
    ],
    highlighted: true,
  },
  {
    key: 'copilot',
    name: 'Copilot',
    tagline: 'Autonomous campaigns.',
    features: [
      'Everything in Content',
      'Autonomous campaigns',
      'Predictive forecasts',
      'Unlimited workspaces',
    ],
  },
];

function useCheckoutSession() {
  return useMutation({
    mutationFn: (body: CheckoutBody) =>
      apiPost<CheckoutResponse>('/v1/billing/checkout-session', body),
  });
}

export function BillingRoute(): JSX.Element {
  const tenant = useAuthStore((s) => s.tenant);
  const setMe = useAuthStore((s) => s.setMe);
  const checkout = useCheckoutSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    status?: 'success' | 'cancel';
  };
  const currentPlan = tenant?.plan ?? 'trial';

  // Stripe return leg. The app writes ?status=success|cancel into the
  // checkout URLs — consume it: confirm the outcome, refetch /v1/me so the
  // plan badge reflects the upgrade (the auth store is never refetched
  // otherwise), then strip the param.
  useEffect(() => {
    if (!search.status) return;
    if (search.status === 'success') {
      sonnerToast.success('Payment complete', {
        description: 'Updating your plan…',
      });
      void apiGet<MeResponse>('/v1/me')
        .then((me) =>
          setMe({
            user: {
              id: me.user.id,
              email: me.user.email,
              display_name: me.user.display_name,
            },
            tenant: {
              id: me.tenant.id,
              name: me.tenant.name,
              slug: me.tenant.slug,
              plan: me.tenant.plan,
              feature_flags: me.tenant.feature_flags,
            },
            role: me.role as MemberRole,
          }),
        )
        .catch(() => {
          /* badge refresh is best-effort; a reload will pick it up */
        });
    } else {
      sonnerToast.info('Checkout cancelled', {
        description: 'Your plan is unchanged.',
      });
    }
    void navigate({ search: {} as never, replace: true });
  }, [search.status, setMe, navigate]);

  const upgrade = async (plan: CheckoutBody['plan']): Promise<void> => {
    try {
      const res = await checkout.mutateAsync({
        plan,
        success_url: `${window.location.origin}/settings/billing?status=success`,
        cancel_url: `${window.location.origin}/settings/billing?status=cancel`,
      });
      window.location.href = res.url;
    } catch (e) {
      toastError(toast, e);
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Settings"
        title="Billing"
        description="Manage your subscription and unlock more agents."
      />
      <SettingsTabs />

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              Current plan
            </SectionTitle>
            <SectionDescription>
              Pick a tier that matches how autonomous you want SEO to be.
            </SectionDescription>
          </div>
          <Badge variant="secondary" className="capitalize">
            {currentPlan}
          </Badge>
        </SectionHeader>

        <div className="grid gap-4 md:grid-cols-3">
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.key;
            return (
              <Surface
                key={plan.key}
                tone={plan.highlighted ? 'raised' : 'default'}
                className={cn(
                  'flex flex-col gap-4 p-5',
                  plan.highlighted && 'ring-1 ring-primary/40',
                )}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{plan.name}</h3>
                    {plan.highlighted ? (
                      <Badge variant="default" className="gap-1">
                        <Sparkles className="h-3 w-3" /> Popular
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.tagline}</p>
                </div>

                <ul className="space-y-1.5 text-sm">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="mt-auto w-full"
                  variant={plan.highlighted ? 'default' : 'outline'}
                  disabled={checkout.isPending || isCurrent}
                  onClick={() => upgrade(plan.key)}
                >
                  {checkout.isPending ? <Spinner className="mr-2" /> : null}
                  {isCurrent ? 'Current plan' : `Upgrade to ${plan.name}`}
                </Button>
              </Surface>
            );
          })}
        </div>
      </Section>
    </Page>
  );
}
